import { Box, Button, HStack, Image, Stack, Text } from '@chakra-ui/react';
import { getAsset, TenantId } from '@pelicargo/tenants';
import { Show, useError } from '@pelicargo/ui';
import { modelUtils } from '@pelicargo/utils';
import { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFormContext, useWatch } from 'react-hook-form';

import { useUploadCustomerAsset } from '../../hooks/tenant/useUploadCustomerAsset';
import { SimpleInput } from '../SimpleInput';

const {
  CUSTOMER_ASSET_NONE,
  CUSTOMER_ASSET_SLOTS,
  customerAssetMediaKind,
  customerAssetMimeTypes,
  resolveCustomerAsset,
} = modelUtils.customerTheme;

type CustomerAssetSlot = modelUtils.customerTheme.CustomerAssetSlot;

type Props = {
  name: string;
  label: string;
  slot: CustomerAssetSlot;
  tenantId?: TenantId;
};

/**
 * One asset slot: a preview of what the slot currently resolves to, an upload that replaces it, and
 * the URL the override actually stores.
 *
 * The slot has three states, and the middle one is the reason this is not just a file input:
 *
 *   - empty      -> the file the tenant ships is used
 *   - a URL      -> that image is used instead
 *   - `none`     -> nothing renders at all
 *
 * "Remove override" and "Remove image" are therefore different buttons with different outcomes,
 * and conflating them is the bug this layout exists to prevent: clearing the field restores the
 * shipped default, it does not take the image away.
 *
 * The upload does not save. It writes the returned public URL into the form field and marks it
 * dirty, so the object exists in storage but nothing reaches a customer until the theme is saved.
 *
 * The URL field stays visible rather than hidden behind the upload: re-pointing a slot at an asset
 * that is already hosted somewhere is a legitimate thing to want, and it needs no upload.
 */
export const ThemeAssetInput = ({ name, label, slot, tenantId }: Props) => {
  const { setValue } = useFormContext();
  const { handleError } = useError();
  const { isUploading, upload } = useUploadCustomerAsset(tenantId);

  const value = useWatch({ name });
  const override = typeof value === 'string' ? value.trim() : '';

  const isRemoved = override === CUSTOMER_ASSET_NONE;
  const hasOverride = override.length > 0 && !isRemoved;

  const mediaKind = customerAssetMediaKind(slot);
  const defaultFilename = CUSTOMER_ASSET_SLOTS[slot];

  // Previewing exactly what the customer app will resolve, through the same function it uses, so
  // the preview cannot claim one thing while the app renders another - including rendering nothing.
  const previewSrc = useMemo(() => {
    const resolved = resolveCustomerAsset(slot, override);
    if (resolved.kind === 'none') return undefined;

    return resolved.kind === 'external' ? resolved.url : getAsset(resolved.filename);
  }, [slot, override]);

  const handleUpload = async (files: File[]) => {
    const file = files?.[0];
    if (!file) return;

    try {
      const url = await upload(slot, file);
      setValue(name, url, { shouldDirty: true, shouldValidate: true });
    } catch (error) {
      handleError(error);
    }
  };

  // `noClick`/`noKeyboard` because the visible affordance is the button below, which calls `open()`
  // itself - leaving them on would make a click anywhere in the preview row open a file dialog.
  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: handleUpload,
    accept: Object.fromEntries(customerAssetMimeTypes(slot).map((mime) => [mime, []])),
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: isUploading || !tenantId,
  });

  // Back to the shipped default. Used by both "Remove override" and "Restore default" - they are
  // the same operation from the two states that can be left.
  const handleClearOverride = () => setValue(name, '', { shouldDirty: true, shouldValidate: true });

  const handleRemoveImage = () => setValue(name, CUSTOMER_ASSET_NONE, { shouldDirty: true, shouldValidate: true });

  const showVideoPreview = mediaKind === 'video' && !isRemoved;
  const showImagePreview = mediaKind !== 'video' && !isRemoved;
  const uploadLabel = hasOverride ? 'Replace' : 'Upload';

  return (
    <Stack spacing="2" {...getRootProps()}>
      <input {...getInputProps()} />

      <HStack align="flex-start" spacing="3">
        <Box
          w="16"
          h="16"
          flexShrink="0"
          mt="6"
          borderRadius="md"
          borderWidth="1px"
          borderColor={isDragActive ? 'blue.300' : 'blackAlpha.400'}
          borderStyle={isDragActive ? 'dashed' : 'solid'}
          overflow="hidden"
          bg="blackAlpha.200"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Show if={showVideoPreview}>
            <Box as="video" src={previewSrc} w="full" h="full" objectFit="cover" muted loop autoPlay playsInline />
          </Show>
          <Show if={showImagePreview}>
            <Image src={previewSrc} w="full" h="full" objectFit="contain" alt={`${label} preview`} />
          </Show>
          <Show if={isRemoved}>
            <Text fontSize="10px" opacity="0.6" textAlign="center">
              No image
            </Text>
          </Show>
        </Box>

        <Box flex="1" minW="0">
          <SimpleInput
            name={name}
            label={label}
            placeholder={`Default: ${defaultFilename}`}
            helper="Upload a file, or paste a URL"
          />
        </Box>
      </HStack>

      <HStack spacing="2">
        <Button size="xs" variant="outline" isLoading={isUploading} isDisabled={!tenantId} onClick={open}>
          {uploadLabel}
        </Button>

        {/* Only offered when there is an override to drop - from the removed state the same
            operation is spelled "Restore default", because that is what it does from there. */}
        <Show if={hasOverride}>
          <Button size="xs" variant="ghost" onClick={handleClearOverride}>
            Remove override
          </Button>
        </Show>

        <Show if={!isRemoved}>
          <Button size="xs" variant="ghost" colorScheme="red" onClick={handleRemoveImage}>
            Remove image
          </Button>
        </Show>

        <Show if={isRemoved}>
          <Button size="xs" variant="ghost" onClick={handleClearOverride}>
            Restore default
          </Button>
        </Show>
      </HStack>
    </Stack>
  );
};
